import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Chip,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import InfiniteScroll from "react-infinite-scroll-component";
import { useOutletContext } from "react-router-dom";
import ExploreSecondaryHeader from "./SecondaryHeader";
import CollectionCover from "../curate/Cover.js";
import Loading from "../../components/Loading";
import Offline from "../../components/Offline";
import { EXPLORE_SECTIONS } from "./exploreSections";
import useApi from "../../hooks/useApi";
import { ExpandableTypography } from "../../components/ExpandableTypography";
import { pluralize } from "../../utils/utils";
import MotionInView from "../../components/MotionInView";
import { useInitialBatchSize } from "../../hooks/useInitialBatchSize";
import { useFillViewport } from "../../hooks/useFillViewport";
import { useDetailNavigation } from "../../hooks/useDetailNavigation";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";

// A role only earns a filter pill once it has enough collections behind it to be
// worth filtering to — below this the pill just leads to a near-empty page.
const MIN_COLLECTIONS_PER_ROLE = 3;

/**
 * Drill-down page for a single collection section, e.g. /explore/critics-favourites.
 * Renders an infinite-scroll list of all matching canonical collections using the
 * same tag filter defined in the section registry.
 */
export default function ExploreCollectionPage({
  collectionType,
  contentTypeFilter = "all",
}) {
  const { toggleDrawer, drawerWidth } = useOutletContext();
  const theme = useTheme();
  const fullscreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { fetchExploreCollections, fetchExploreCollectionFacets } = useApi();
  const { openList } = useDetailNavigation();

  // Look up the section definition from the registry
  const section = EXPLORE_SECTIONS.find(
    (s) => s.type === "collection" && s.slug === collectionType,
  );

  // A page-specific title so search engines don't see every section page as a
  // duplicate of the app-wide default (these pages are in the sitemap)
  useDocumentMeta({
    title: section ? `${section.title}: collections on Curate` : undefined,
  });

  // Title search — narrows the collections list, mirrors the curate route's pattern
  const [searchTerm, setSearchTerm] = useState("");

  // Role filter pills — only the "As Recommended By" section has role-kind tags
  // to filter on, but they apply across all content types (author/director/
  // tastemaker are content-type-neutral).
  const supportsRoleFilter = collectionType === "recommended-by";

  // Selected role slug, or null for "All". Reset whenever the section stops
  // supporting the filter, or the current selection falls out of scope (e.g.
  // switching content type to one where that role has too few collections),
  // so a stale role filter doesn't linger and silently render an empty page.
  const [selectedRole, setSelectedRole] = useState(null);

  const { data: roleFacets } = useQuery({
    queryKey: ["exploreCollectionFacets", collectionType, contentTypeFilter],
    queryFn: () =>
      fetchExploreCollectionFacets({
        kind: "role",
        tagFilter: section?.tagFilter,
        contentType: contentTypeFilter,
      }),
    enabled: supportsRoleFilter,
  });

  // Only surface roles with enough collections to be worth a pill.
  const roles = useMemo(
    () =>
      (roleFacets ?? []).filter(
        (role) => role.collection_count >= MIN_COLLECTIONS_PER_ROLE,
      ),
    [roleFacets],
  );

  useEffect(() => {
    if (!supportsRoleFilter) {
      setSelectedRole(null);
    } else if (selectedRole && !roles.some((r) => r.slug === selectedRole)) {
      setSelectedRole(null);
    }
  }, [supportsRoleFilter, roles, selectedRole]);

  const showRoleFilter = supportsRoleFilter && roles.length > 0;

  // Layer the selected role onto the section's base tag filter at query time —
  // the base filter (e.g. kind:recommended-by) is never mutated.
  const effectiveTagFilter = useMemo(() => {
    const base = section?.tagFilter ?? [];
    return showRoleFilter && selectedRole
      ? [...base, [["role", selectedRole]]]
      : base;
  }, [section, showRoleFilter, selectedRole]);

  const collectionsQuery = useInfiniteQuery({
    queryKey: [
      "infExploreCollections",
      collectionType,
      contentTypeFilter,
      searchTerm,
      selectedRole,
    ],
    queryFn: ({ pageParam = 1 }) =>
      fetchExploreCollections({
        tagFilter: effectiveTagFilter,
        contentType: contentTypeFilter,
        searchTerm,
        pageParam,
        perPage: 10,
        // Cap each list's items to the 5 posters we render; the response still
        // carries the true item_count and full-list finished_count for the
        // card's "% finished" (see CollectionCover).
        maxItems: 5,
        sortBy: section?.sortBy,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!section,
  });

  const collections = useMemo(
    () => collectionsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [collectionsQuery.data],
  );

  useFillViewport(collectionsQuery);

  // Only the batch loaded on first render of the current filter context gets
  // an entrance animation — cards appended afterward via infinite scroll (or
  // the current batch, once already settled) should never animate again.
  const collectionsGenerationKey = JSON.stringify([
    collectionType,
    contentTypeFilter,
    searchTerm,
    selectedRole,
  ]);
  const initialBatchSize = useInitialBatchSize(
    collections,
    collectionsGenerationKey,
  );

  return (
    <Container maxWidth="lg" disableGutters sx={{ pb: 2 }}>
      <ExploreSecondaryHeader
        toggleDrawer={toggleDrawer}
        drawerWidth={drawerWidth}
        activeFilter={contentTypeFilter}
        basePath={`/explore/${collectionType}`}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
      />

      {/* Section header */}
      <Box
        sx={{
          p: 1,
          bgcolor: "secondary.main",
          mb: 2,
          backgroundImage: "none",
          borderRadius: 1,
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {section?.title ?? collectionType}
        </Typography>
        {section?.pageDescription && (
          <ExpandableTypography
            text={section.pageDescription}
            lineClamp={3}
            variant="body2"
            whiteSpace="pre-wrap"
            bgColor={theme.palette.secondary.main}
          />
        )}
      </Box>

      {/* Role filter pills — recommended-by, any content type, roles with enough
          collections to clear MIN_COLLECTIONS_PER_ROLE (see showRoleFilter) */}
      {showRoleFilter && (
        <Box sx={{ position: "relative", mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": { display: "none" },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            <Chip
              label="All"
              onClick={() => setSelectedRole(null)}
              color={selectedRole === null ? "primary" : "default"}
              variant={selectedRole === null ? "filled" : "outlined"}
              sx={{
                flexShrink: 0,
                // Stack above the left edge-fade gradient: it's the true start of
                // the row, so it shouldn't read as faded like there's more before it.
                position: "relative",
                zIndex: 20,
              }}
            />
            {roles.map((role, index) => {
              const active = selectedRole === role.slug;
              const isLast = index === roles.length - 1;
              return (
                <Chip
                  key={role.id}
                  label={pluralize(role.display_name)}
                  onClick={() => setSelectedRole(active ? null : role.slug)}
                  color={active ? "primary" : "default"}
                  variant={active ? "filled" : "outlined"}
                  sx={{
                    flexShrink: 0,
                    // Stack above the edge-fade gradient: it's the true end of the
                    // row, so it shouldn't read as faded like there's more to scroll.
                    ...(isLast && { position: "relative", zIndex: 20 }),
                  }}
                />
              );
            })}
          </Box>

          {/* Hints that the row scrolls further right; the last pill (above) is
              exempted so the true end of the list never looks cut off. */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: 0,
              bottom: 8, // clears the row's pb: 1 so it doesn't fade the empty gutter
              right: 0,
              width: 40,
              pointerEvents: "none",
              zIndex: 10,
              background: (theme) =>
                `linear-gradient(to right, transparent, ${theme.palette.background.default})`,
            }}
          />

          {/* Mirrors the right-edge fade on the left; the "All" pill (above) is
              exempted so the true start of the list never looks cut off. */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: 0,
              bottom: 8,
              left: 0,
              width: 40,
              pointerEvents: "none",
              zIndex: 10,
              background: (theme) =>
                `linear-gradient(to left, transparent, ${theme.palette.background.default})`,
            }}
          />
        </Box>
      )}

      {collectionsQuery.isError && <Offline />}

      {collectionsQuery.isPending && (
        <Box
          width="100%"
          height="75dvh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Loading />
        </Box>
      )}

      {collectionsQuery.isSuccess && collections.length === 0 && (
        <Typography sx={{ px: { xs: 2, sm: 3 }, pt: 2 }} color="text.secondary">
          No collections found.
        </Typography>
      )}

      {collectionsQuery.isSuccess && collections.length > 0 && (
        <InfiniteScroll
          dataLength={collections.length}
          next={collectionsQuery.fetchNextPage}
          hasMore={!!collectionsQuery.hasNextPage}
          loader={
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Loading inline />
            </Box>
          }
          style={{ overflow: "visible" }}
        >
          <Grid container sx={{ pt: 1, pb: 2 }} spacing={2}>
            {collections.map((list, index) => {
              const card = (
                <CollectionCover
                  list={list}
                  reverse={index % 2 === 0}
                  onClick={() => openList(list)}
                  isMobile={fullscreen}
                />
              );
              return (
                <Grid item xs={12} key={list.list_id}>
                  {index < initialBatchSize ? (
                    <MotionInView inView delay={Math.min(index, 6) * 0.05}>
                      {card}
                    </MotionInView>
                  ) : (
                    card
                  )}
                </Grid>
              );
            })}
          </Grid>
        </InfiniteScroll>
      )}
    </Container>
  );
}
